import { useCallback, useEffect, useRef, useState } from 'react';
import { listAccounts } from '../../services/AccountsService';

export const LIVE_MS = 30_000;
const MAX_BACKOFF_MS = 5 * 60_000;
const STALE_ON_FOCUS_MS = 2_000;

/** "just now", "12 s ago", "3 min ago", "2 h ago" (Console Next's live indicator wording). */
export function updatedAgo(at, now) {
  if (at === undefined || at === null) { return 'not yet'; }
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 2) { return 'just now'; }
  if (seconds < 60) { return `${seconds} s ago`; }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) { return `${minutes} min ago`; }
  return `${Math.round(minutes / 60)} h ago`;
}

export const nextDelay = (interval, failures) => Math.min(MAX_BACKOFF_MS, interval * 2 ** Math.min(Math.max(failures, 0), 8));

/**
 * The person's accounts, kept current: read now, then every `interval` while the tab is visible (setTimeout, the next
 * read only after the previous answered; a failure doubles the wait, up to 5 min); on focus or when the tab shows
 * again, read at once if the data is older than 2 s. Background reads replace the data only when it changed.
 * Returns `{ data, error, loading, updatedAt, failures, hidden, refresh }`.
 */
export default function useAccounts({ interval = LIVE_MS, load = listAccounts } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(undefined);
  const [failures, setFailures] = useState(0);
  const [hidden, setHidden] = useState(typeof document !== 'undefined' && document.visibilityState === 'hidden');
  const failuresRef = useRef(0);
  const generation = useRef(0);
  const lastJson = useRef('');
  const updatedRef = useRef(0);

  const refresh = useCallback(async() => {
    const mine = ++generation.current;
    try {
      const next = await load();
      if (mine !== generation.current) { return; }
      const json = JSON.stringify(next);
      if (json !== lastJson.current) {
        lastJson.current = json;
        setData(next);
      }
      setError(null);
      failuresRef.current = 0;
      setFailures(0);
      updatedRef.current = Date.now();
      setUpdatedAt(updatedRef.current);
    } catch (err) {
      if (mine !== generation.current) { return; }
      setError(err);
      failuresRef.current += 1;
      setFailures(failuresRef.current);
    } finally {
      if (mine === generation.current) { setLoading(false); }
    }
  }, [load]);

  useEffect(() => {
    let timer;
    let stopped = false;
    const schedule = () => {
      clearTimeout(timer);
      if (stopped) { return; }
      timer = setTimeout(run, nextDelay(interval, failuresRef.current));
    };
    const run = () => {
      if (stopped) { return; }
      if (document.visibilityState === 'hidden') { schedule(); return; }
      refresh().finally(schedule);
    };
    refresh().finally(schedule);
    const onResume = () => {
      const isHidden = document.visibilityState === 'hidden';
      setHidden(isHidden);
      if (!isHidden && Date.now() - updatedRef.current > STALE_ON_FOCUS_MS) { refresh().finally(schedule); }
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
    };
  }, [interval, refresh]);

  return { data, error, loading, updatedAt, failures, hidden, refresh };
}

/** Whether Flow's dark theme is on (utils/themeMode.js puts `dark-mode` on <html>), following changes. */
export function useIsDark() {
  const read = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark-mode');
  const [dark, setDark] = useState(read);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') { return undefined; }
    const observer = new MutationObserver(() => setDark(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

/** Whether the viewport is phone-sized (below 768px), following changes. */
export function useNarrow(query = '(max-width: 767px)') {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.(query).matches));
  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) { return undefined; }
    const onChange = () => setNarrow(list.matches);
    list.addEventListener?.('change', onChange);
    return () => list.removeEventListener?.('change', onChange);
  }, [query]);
  return narrow;
}
